/**
 * Display metadata for the four per-machine key metrics the demo charts.
 *
 * Source: `const METRIC_INFO={ … }` at line 2130 of
 * `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`.
 *
 * Porting decisions
 * - The demo object is already keyed by metric id, so it maps straight onto a
 *   `Record`. The four keys are the only metrics the seed data carries, so the
 *   key type is a closed union derived from `METRIC_KEYS` (`as const`).
 * - `limit` is `number | null`; `null` means "no alarm line on the mini chart"
 *   and the demo tests it with `mi.limit ? …` when labelling.
 * - `note` is carried through verbatim even though the demo's `machineCard()`
 *   renderer does not currently print it (only `name`, `unit` and `limit` are
 *   used). It documents how the daily value is derived, so a later scene can
 *   surface it as a chart subtitle.
 * - Unicode preserved exactly: `°C` in the unit strings.
 * - No markup and no handlers in this constant.
 *
 * Data Schema layer — pure data, no behaviour.
 */

/** The metric ids present in the demo seed, in source order. */
export const METRIC_KEYS = [
  'penetration_rate_m_h',
  'percussion_pressure_bar',
  'hyd_oil_temp_c',
  'rear_brake_temp_c',
] as const;

/** A key of `METRIC_INFO`, e.g. `'hyd_oil_temp_c'`. */
export type MetricKey = (typeof METRIC_KEYS)[number];

/** How one daily key metric is labelled and bounded. */
export interface MetricInfo {
  /** Human label, e.g. `'Hydraulic oil temperature'`. */
  readonly name: string;
  /** Unit suffix printed next to the value, e.g. `'°C'`. */
  readonly unit: string;
  /** Alarm/limit line drawn on the mini chart, or `null` when there is none. */
  readonly limit: number | null;
  /** How the daily figure is derived. */
  readonly note: string;
}

/** Metric display metadata, keyed by metric id. */
export const METRIC_INFO: Readonly<Record<MetricKey, MetricInfo>> = {
  penetration_rate_m_h: {
    name: 'Penetration rate',
    unit: 'm/h',
    limit: null,
    note: 'daily mean while drilling (RMS)',
  },
  percussion_pressure_bar: {
    name: 'Percussion pressure',
    unit: 'bar',
    limit: null,
    note: 'daily mean at full collar',
  },
  hyd_oil_temp_c: {
    name: 'Hydraulic oil temperature',
    unit: '°C',
    limit: 85,
    note: 'daily mean, shift end',
  },
  rear_brake_temp_c: {
    name: 'Rear brake temperature',
    unit: '°C',
    limit: 240,
    note: 'daily mean on loaded descents',
  },
};

/**
 * Fallback the demo uses when a machine's metric id is not in `METRIC_INFO`
 * (`const mi=METRIC_INFO[metric]||{name:metric,unit:'',limit:null,note:''}`).
 */
export const metricInfoFor = (metric: string): MetricInfo =>
  (METRIC_INFO as Readonly<Record<string, MetricInfo | undefined>>)[metric] ?? {
    name: metric,
    unit: '',
    limit: null,
    note: '',
  };
